import React, { useMemo, useState } from "react";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import { X, Mail, FileText, Repeat, Megaphone, Reply, Send } from "lucide-react";
import FollowUpComposerModal from "@/hq/clients/FollowUpComposerModal";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Map comm_type → curated label + filter group + icon
const TYPE_META = {
  intro:                     { label: "Introduction",       group: "emails",         Icon: Mail },
  intro_email:               { label: "Introduction",       group: "emails",         Icon: Mail },
  client_intro:              { label: "Introduction",       group: "emails",         Icon: Mail },
  website_preview:           { label: "Website Preview",    group: "emails",         Icon: Mail },
  client_website_preview:    { label: "Website Preview",    group: "emails",         Icon: Mail },
  hq_subscription_setup:     { label: "Subscription Setup", group: "subscriptions",  Icon: Repeat },
  subscription_activated:    { label: "Subscription Activated", group: "subscriptions", Icon: Repeat },
  invoice_payment_request:   { label: "Payment Request",    group: "invoices",       Icon: FileText },
  invoice_payment_received:  { label: "Payment Receipt",    group: "invoices",       Icon: FileText },
  campaign_send:             { label: "Campaign Email",     group: "campaigns",      Icon: Megaphone },
  sales_campaign:            { label: "Campaign Email",     group: "campaigns",      Icon: Megaphone },
  manual:                    { label: "Manual Email",       group: "emails",         Icon: Mail },
};

const STATUS_TONE = {
  sent:      { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  delivered: { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  sending:   { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  queued:    { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  draft:     { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
  skipped:   { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
  failed:    { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7" },
};

const FILTERS = [
  { key: "all",           label: "All" },
  { key: "emails",        label: "Emails" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "invoices",      label: "Invoices" },
  { key: "campaigns",     label: "Campaigns" },
  { key: "failed",        label: "Failed" },
];

function meta(comm) {
  return TYPE_META[(comm.comm_type || "").toLowerCase()]
    || { label: (comm.comm_type || "email").replace(/_/g, " "), group: "emails", Icon: Mail };
}

function relatedLabel(comm) {
  const md = comm.metadata || {};
  if (md.invoice_number) return `Invoice ${md.invoice_number}`;
  if (md.invoice_id) return `Invoice ${String(md.invoice_id).slice(0, 8)}`;
  if (md.subscription_id) return `Subscription ${String(md.subscription_id).slice(0, 8)}`;
  if (md.campaign_name) return `Campaign · ${md.campaign_name}`;
  return null;
}

function StatusPill({ status }) {
  const key = (status || "").toLowerCase();
  const tone = STATUS_TONE[key] || STATUS_TONE.draft;
  return (
    <span
      data-testid={`hq-comm-status-${key}`}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black tracking-[0.16em] uppercase"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
    >
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

function ViewEmailModal({ comm, onClose }) {
  if (!comm) return null;
  const html = (comm.metadata || {}).html || comm.html;
  const text = (comm.metadata || {}).text || comm.text;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-comm-view-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{meta(comm).label}</div>
            <h2 className="font-black text-base mt-1">{comm.subject || "(no subject)"}</h2>
            <div className="text-[11px] text-[#8A8A8A]">To {comm.to_email || "—"} · {comm.sent_at || comm.created_at ? new Date(comm.sent_at || comm.created_at).toLocaleString() : "—"}</div>
          </div>
          <button data-testid="hq-comm-view-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={16}/></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          {html
            ? <iframe title="Email content" srcDoc={html} data-testid="hq-comm-view-iframe"
                className="w-full h-[520px] border border-[#EAEAEA] rounded-xl bg-white"/>
            : text
              ? <pre className="text-[13px] whitespace-pre-wrap font-sans">{text}</pre>
              : <div className="text-[13px] text-[#8A8A8A]">The full body wasn't stored for this record. Metadata:
                  <pre className="mt-2 text-[11px] bg-[#F6F5F2] p-3 rounded-lg overflow-auto">{JSON.stringify(comm.metadata || {}, null, 2)}</pre>
                </div>}
        </div>
      </div>
    </div>
  );
}

/**
 * ClientCommsTimeline — Phase 2D.
 *
 * Reads from `core_communications` (returned in the client-profile
 * response). One curated timeline of every subscription/invoice/intro/
 * preview/campaign/manual email tied to this client. Filter + View.
 */
export default function ClientCommsTimeline({ communications, businessId, businessName, primaryContact, primaryEmail, onFollowupSent }) {
  const [filter, setFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [followupFor, setFollowupFor] = useState(null); // null or { subject, related_label, in_reply_to, ... }

  const rows = useMemo(() => {
    const list = [...(communications || [])];
    return list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [communications]);

  const filtered = useMemo(() => rows.filter((c) => {
    if (filter === "all") return true;
    if (filter === "failed") return (c.status || "").toLowerCase() === "failed";
    return meta(c).group === filter;
  }), [rows, filter]);

  const failedCount = rows.filter((c) => (c.status || "").toLowerCase() === "failed").length;

  return (
    <Card className="p-6" data-testid="hq-client-comms">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Communications</div>
          <p className="text-sm text-[#4A4A4A] mt-1">Every email sent to this client — introductions, subscription setups, invoices, campaigns and receipts.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`hq-comm-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${filter === f.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"}`}
            >
              {f.label}{f.key === "failed" && failedCount > 0 ? ` · ${failedCount}` : ""}
            </button>
          ))}
          {businessId && (
            <button
              data-testid="hq-comm-send-followup"
              onClick={() => setFollowupFor({})}
              className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
            >
              <Send size={11}/> Send follow-up
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6"><EmptyState title="No communications" hint="Sent emails will appear here." testId="hq-client-comms-empty"/></div>
      ) : (
        <div className="mt-5 space-y-2" data-testid="hq-client-comms-list">
          {filtered.map((c) => {
            const m = meta(c);
            const rel = relatedLabel(c);
            return (
              <div key={c.id} className="border border-[#EAEAEA] rounded-xl p-3 hover:bg-[#FFFDF6]" data-testid={`hq-comm-row-${c.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] flex items-center justify-center text-[#FF6A00] shrink-0">
                    <m.Icon size={14}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] tracking-[0.18em] uppercase font-black text-[#8A4A00]">{m.label}</span>
                      <StatusPill status={c.status}/>
                      {rel && <span className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#8A8A8A]">· {rel}</span>}
                    </div>
                    <div className="mt-1 font-bold text-[14px] truncate">{c.subject || "(no subject)"}</div>
                    <div className="text-[11px] text-[#8A8A8A]">To {c.to_email || "—"} · {c.sent_at || c.created_at ? new Date(c.sent_at || c.created_at).toLocaleString() : "—"}</div>
                  </div>
                  <button
                    data-testid={`hq-comm-view-${c.id}`}
                    onClick={() => setViewing(c)}
                    className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase border border-[#EAEAEA] hover:border-[#FF6A00] hover:text-[#FF6A00]"
                  >
                    View
                  </button>
                  {businessId && (
                    <button
                      data-testid={`hq-comm-followup-${c.id}`}
                      onClick={() => setFollowupFor({
                        initialSubject: c.subject ? (c.subject.toLowerCase().startsWith("re:") ? c.subject : `Re: ${c.subject}`) : "",
                        initialRelatedLabel: rel,
                        relatedType: (c.metadata || {}).invoice_id ? "invoice"
                                    : (c.metadata || {}).subscription_id ? "subscription"
                                    : (c.metadata || {}).campaign_id ? "campaign" : null,
                        relatedId: (c.metadata || {}).invoice_id
                                  || (c.metadata || {}).subscription_id
                                  || (c.metadata || {}).campaign_id || null,
                        inReplyTo: c.id,
                      })}
                      className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase text-[#FF6A00] hover:bg-[#FFF7EE]"
                    >
                      <Reply size={11} className="inline mr-1"/>Follow up
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ViewEmailModal comm={viewing} onClose={() => setViewing(null)}/>
      <FollowUpComposerModal
        open={!!followupFor}
        onClose={() => setFollowupFor(null)}
        businessId={businessId}
        businessName={businessName}
        primaryContact={primaryContact}
        primaryEmail={primaryEmail}
        initialSubject={followupFor?.initialSubject}
        initialRelatedLabel={followupFor?.initialRelatedLabel}
        relatedType={followupFor?.relatedType}
        relatedId={followupFor?.relatedId}
        inReplyTo={followupFor?.inReplyTo}
        onSent={() => { setFollowupFor(null); onFollowupSent?.(); }}
      />
    </Card>
  );
}
